import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import LangLink from '../../components/common/LangLink';
import { langPath } from '../../utils/langPath';
import { Lock, ArrowLeft, Loader2, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { userService } from '../../services/api';
import SEOHead from '../../components/common/SEOHead';
import AuthLayout, { AUTH_INPUT_CLASS, AUTH_BUTTON_CLASS } from './AuthLayout';

const MIN_PASSWORD_LENGTH = 6;

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { language } = useLanguage();
    const fr = language === 'fr';
    const token = searchParams.get('token') || '';

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [status, setStatus] = useState('idle');
    const [errorMsg, setErrorMsg] = useState('');

    const tokenLooksValid = /^[a-f0-9]{64}$/.test(token);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');

        if (password.length < MIN_PASSWORD_LENGTH) {
            setErrorMsg(fr
                ? `Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`
                : `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
            return;
        }
        if (password !== confirmPassword) {
            setErrorMsg(fr ? 'Les mots de passe ne correspondent pas.' : 'Passwords do not match.');
            return;
        }

        setStatus('loading');
        try {
            await userService.resetPassword(token, password, language);
            setStatus('success');
            setTimeout(() => navigate(langPath('/login', language) + '?passwordReset=true'), 2000);
        } catch (err) {
            setStatus('error');
            const apiErr = err.response?.data?.error;
            setErrorMsg(apiErr || (fr ? 'Erreur réseau. Veuillez réessayer.' : 'Network error. Please try again.'));
        }
    };

    if (!tokenLooksValid) {
        return (
            <AuthLayout
                title={fr ? 'Lien invalide' : 'Invalid link'}
                subtitle={fr
                    ? 'Le lien de réinitialisation est invalide ou incomplet.'
                    : 'The reset link is invalid or incomplete.'}
                footer={
                    <LangLink to="/login" className="inline-flex items-center gap-1.5 text-sm text-[#0061FE] dark:text-[#71a7ff] hover:text-[#0052db] dark:hover:text-white font-medium transition">
                        <ArrowLeft size={14} />
                        {fr ? 'Retour à la connexion' : 'Back to login'}
                    </LangLink>
                }
            >
                <SEOHead title={fr ? 'Lien invalide' : 'Invalid link'} canonical="/reset-password" noindex />
                <LangLink to="/forgot-password" className={AUTH_BUTTON_CLASS}>
                    {fr ? 'Demander un nouveau lien' : 'Request a new link'}
                </LangLink>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            title={fr ? 'Réinitialisation' : 'Reset password'}
            subtitle={status === 'success' ? null : (fr
                ? 'Choisissez un nouveau mot de passe pour votre compte.'
                : 'Choose a new password for your account.')}
            footer={
                <LangLink to="/login" className="inline-flex items-center gap-1.5 text-sm text-[#0061FE] dark:text-[#71a7ff] hover:text-[#0052db] dark:hover:text-white font-medium transition">
                    <ArrowLeft size={14} />
                    {fr ? 'Retour à la connexion' : 'Back to login'}
                </LangLink>
            }
        >
            <SEOHead title={fr ? 'Réinitialiser le mot de passe' : 'Reset password'} canonical="/reset-password" noindex />

            {status === 'success' ? (
                <div className="text-center">
                    <div className="w-14 h-14 bg-green-50 dark:bg-green-500/15 border border-green-200 dark:border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle size={26} className="text-green-600 dark:text-green-400" />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-[#a9b1bd] mb-6">
                        {fr
                            ? 'Mot de passe mis à jour. Redirection vers la connexion…'
                            : 'Password updated. Redirecting to login…'}
                    </p>
                </div>
            ) : (
                <>
                    <div className="w-14 h-14 bg-[#0061FE]/10 dark:bg-[#0061FE]/15 border border-[#0061FE]/20 dark:border-[#0061FE]/30 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Lock size={24} className="text-[#0061FE] dark:text-[#71a7ff]" />
                    </div>

                    {errorMsg && (
                        <div className="mb-5 p-3.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl">
                            <p className="text-sm text-red-700 dark:text-red-300">{errorMsg}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label htmlFor="new-password" className="block text-xs font-semibold text-gray-700 dark:text-white/70 mb-2 uppercase tracking-wider">
                                {fr ? 'Nouveau mot de passe' : 'New password'}
                            </label>
                            <div className="relative">
                                <input
                                    id="new-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required minLength={MIN_PASSWORD_LENGTH}
                                    className={`${AUTH_INPUT_CLASS} pr-11`}
                                    placeholder={fr ? `Au moins ${MIN_PASSWORD_LENGTH} caractères` : `At least ${MIN_PASSWORD_LENGTH} characters`}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(v => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-white/40 hover:text-gray-700 dark:hover:text-white transition"
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="confirm-password" className="block text-xs font-semibold text-gray-700 dark:text-white/70 mb-2 uppercase tracking-wider">
                                {fr ? 'Confirmer le mot de passe' : 'Confirm password'}
                            </label>
                            <input
                                id="confirm-password"
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required minLength={MIN_PASSWORD_LENGTH}
                                className={AUTH_INPUT_CLASS}
                                placeholder="••••••••"
                                autoComplete="new-password"
                            />
                        </div>

                        <button type="submit" disabled={status === 'loading'} className={`${AUTH_BUTTON_CLASS} gap-2`}>
                            {status === 'loading' ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    {fr ? 'Mise à jour…' : 'Updating…'}
                                </>
                            ) : (
                                fr ? 'Réinitialiser' : 'Reset password'
                            )}
                        </button>
                    </form>
                </>
            )}
        </AuthLayout>
    );
}
